import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import {
  CLOSE_AT_CAPACITY,
  CLOSE_UNAUTHORIZED,
  type ClientMessage,
  type Member,
  type Pin,
  type PinDraft,
  type ServerMessage,
} from "@tarkov-callout/shared";
import { socketUrl } from "./api";
import { forgetCredential } from "./bootstrap";
import { describeKey, t } from "./i18n";
import type { AppSession } from "./bootstrap";

/**
 * `full` means the server turned us away on purpose, not that the link broke.
 * `expired` means the credential itself is finished, which reconnecting cannot
 * mend -- the app has to be started again to get a new one.
 */
export type ConnectionStatus = "connecting" | "open" | "closed" | "full" | "expired";

export interface Notice {
  id: number;
  text: string;
}

export interface RoomController {
  status: ConnectionStatus;
  /** Why the server refused us, when it did. */
  statusDetail: string | null;
  mapId: string;
  pins: Pin[];
  members: Member[];
  selfId: string | null;
  notice: Notice | null;
  addPin: (draft: PinDraft) => void;
  movePin: (id: string, lat: number, lng: number, heading?: number | null) => void;
  labelPin: (id: string, label: string) => void;
  /** `via` only reaches the log: which piece of interface asked. */
  removePin: (id: string, via?: "eraser" | "card" | "list" | "undo" | "key") => void;
  setMap: (mapId: string) => void;
  clear: (scope: "mine" | "all") => void;
}

const KEEPALIVE_MS = 30_000;
const RECONNECT_MIN_MS = 1_000;
const RECONNECT_MAX_MS = 15_000;
/** A full server will not empty in a second, so stop hammering it. */
const RECONNECT_FULL_MS = 30_000;

/** At most one automatic reload a minute. */
const RELOAD_KEY = "callout:reloadedAt";
const RELOAD_MIN_GAP_MS = 60_000;

/**
 * Spreads reconnections out instead of firing them on the dot.
 *
 * A restart drops every client at the same instant, and a few hundred of them
 * counting down together would arrive together too -- which is how a server
 * that has just come back goes down again.
 */
function withJitter(delay: number): number {
  return Math.round(delay * (0.7 + Math.random() * 0.6));
}

/**
 * Whether to reboot the app to replace a credential the server has rejected.
 *
 * Rate limited on purpose: if the server refuses whatever we come back with,
 * an unguarded reload turns into a loop that never lets the page settle long
 * enough to show anyone why.
 */
function mayReload(): boolean {
  try {
    const last = Number(window.sessionStorage.getItem(RELOAD_KEY) ?? 0);
    if (Number.isFinite(last) && Date.now() - last < RELOAD_MIN_GAP_MS) return false;
    window.sessionStorage.setItem(RELOAD_KEY, String(Date.now()));
    return true;
  } catch {
    // Storage is walled off in some embeds. Stuck with a message beats looping.
    return false;
  }
}

export function useRoom(session: AppSession): RoomController {
  const [status, setStatus] = useState<ConnectionStatus>("connecting");
  const [statusDetail, setStatusDetail] = useState<string | null>(null);
  const [mapId, setMapId] = useState("customs");
  const [pins, setPins] = useState<Pin[]>([]);
  const [members, setMembers] = useState<Member[]>([]);
  const [selfId, setSelfId] = useState<string | null>(null);
  const [notice, setNotice] = useState<Notice | null>(null);

  const socketRef = useRef<WebSocket | null>(null);
  const noticeSeq = useRef(0);

  const announce = useCallback((text: string) => {
    noticeSeq.current += 1;
    setNotice({ id: noticeSeq.current, text });
  }, []);

  useEffect(() => {
    let disposed = false;
    let retryDelay = RECONNECT_MIN_MS;
    let reconnectTimer: number | undefined;
    let keepaliveTimer: number | undefined;

    const apply = (message: ServerMessage) => {
      switch (message.t) {
        case "welcome":
          setSelfId(message.selfId);
          setMapId(message.state.mapId);
          setPins(message.state.pins);
          setMembers(message.state.members);
          break;
        case "pin.add":
          setPins((current) => [...current.filter((p) => p.id !== message.pin.id), message.pin]);
          break;
        case "pin.update":
          setPins((current) => current.map((p) => (p.id === message.pin.id ? message.pin : p)));
          break;
        case "pin.remove":
          setPins((current) => current.filter((p) => p.id !== message.id));
          break;
        case "map.set":
          setMapId(message.mapId);
          announce(t("notice.mapChanged", { name: message.byName }));
          break;
        case "members":
          setMembers(message.members);
          break;
        case "error":
          announce(message.message);
          break;
      }
    };

    const connect = () => {
      if (disposed) return;
      setStatus("connecting");

      const socket = new WebSocket(socketUrl(session.session, session.instanceId));
      socketRef.current = socket;

      socket.addEventListener("open", () => {
        if (disposed) return;
        retryDelay = RECONNECT_MIN_MS;
        setStatus("open");
        keepaliveTimer = window.setInterval(() => {
          if (socket.readyState === WebSocket.OPEN) {
            socket.send(JSON.stringify({ t: "keepalive" } satisfies ClientMessage));
          }
        }, KEEPALIVE_MS);
      });

      socket.addEventListener("message", (event) => {
        try {
          const message = JSON.parse(String(event.data)) as ServerMessage;
          /*
           * Answered here rather than left to the timer below. A backgrounded
           * tab has its setInterval throttled to once a minute -- the same
           * minute at which the proxy drops a connection it has seen nothing
           * on -- while message events keep arriving at full rate. Replying to
           * the server's frame is what keeps the upstream direction alive for
           * someone who has the activity minimised.
           */
          if (message.t === "keepalive") {
            if (socket.readyState === WebSocket.OPEN) {
              socket.send(JSON.stringify({ t: "keepalive" } satisfies ClientMessage));
            }
            return;
          }
          apply(message);
        } catch {
          // A malformed frame is not worth tearing the connection down for.
        }
      });

      socket.addEventListener("close", (event) => {
        window.clearInterval(keepaliveTimer);
        socketRef.current = null;
        if (disposed) return;

        if (event.code === CLOSE_UNAUTHORIZED) {
          // The cached credential is the thing being refused; without this the
          // reload below would present the same dud again.
          forgetCredential();
          // Reconnecting cannot fix this: the credential we would present is
          // the one that was just refused. Starting the app over is what mints
          // a new one, so do that rather than knock indefinitely.
          setStatus("expired");
          setStatusDetail(describeKey(event.reason, "error.session"));
          if (mayReload()) window.location.reload();
          return;
        }

        if (event.code === CLOSE_AT_CAPACITY) {
          // A deliberate refusal. Say so plainly and check back rarely.
          setStatus("full");
          setStatusDetail(event.reason ? describeKey(event.reason, "toast.busy") : t("toast.busy"));
          reconnectTimer = window.setTimeout(connect, withJitter(RECONNECT_FULL_MS));
          return;
        }

        setStatus("closed");
        setStatusDetail(null);
        reconnectTimer = window.setTimeout(connect, withJitter(retryDelay));
        retryDelay = Math.min(retryDelay * 2, RECONNECT_MAX_MS);
      });
    };

    connect();

    return () => {
      disposed = true;
      window.clearTimeout(reconnectTimer);
      window.clearInterval(keepaliveTimer);
      socketRef.current?.close();
      socketRef.current = null;
    };
  }, [session.session, session.instanceId, announce]);

  const send = useCallback((message: ClientMessage) => {
    const socket = socketRef.current;
    if (socket?.readyState === WebSocket.OPEN) socket.send(JSON.stringify(message));
  }, []);

  // Transient markers vanish on their own; drop them locally too so the pin
  // fades at the same moment for everyone instead of on the server's sweep.
  useEffect(() => {
    const timer = window.setInterval(() => {
      const now = Date.now();
      setPins((current) =>
        current.some((p) => p.expiresAt !== null && p.expiresAt <= now)
          ? current.filter((p) => p.expiresAt === null || p.expiresAt > now)
          : current,
      );
    }, 1_000);
    return () => window.clearInterval(timer);
  }, []);

  return useMemo<RoomController>(
    () => ({
      status,
      statusDetail,
      mapId,
      pins,
      members,
      selfId,
      notice,
      addPin: (pin) => send({ t: "pin.add", pin }),
      movePin: (id, lat, lng, heading) => send({ t: "pin.move", id, lat, lng, heading }),
      labelPin: (id, label) => send({ t: "pin.label", id, label }),
      removePin: (id, via) => send({ t: "pin.remove", id, ...(via ? { via } : {}) }),
      setMap: (nextMapId) => {
        setMapId(nextMapId); // Optimistic: the server echoes it back to everyone.
        send({ t: "map.set", mapId: nextMapId });
      },
      clear: (scope) => send({ t: "clear", scope }),
    }),
    [status, statusDetail, mapId, pins, members, selfId, notice, send],
  );
}
